import React, {useMemo, useState} from "react";
import {
    Alert,
    Box,
    ButtonBase,
    Chip,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    LinearProgress,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography
} from "@mui/material";
import Button from "@mui/material/Button";
import {
    CancelOutlined,
    CheckCircle,
    CheckCircleOutline,
    ContentCopyOutlined,
    ExpandLess,
    ExpandMore,
    ListAltOutlined,
    PlaylistAddCheckOutlined
} from "@mui/icons-material";
import {green, grey, red} from "@mui/material/colors";
import {useSnackbar} from "../../providers/SnackbarProvider";
import {countResults} from "./useDlvTestRun";
import {MONOSPACE_FONT} from "./dlvStyles";
import {AspCodeView} from "./AspCodeView";
import {copyText} from "./copyText";

const CODE_CHIP_SX = {
    maxWidth: '100%',
    height: 'auto',
    py: 0.5,
    '& .MuiChip-label': {fontFamily: MONOSPACE_FONT, fontSize: '0.78rem', whiteSpace: 'normal', wordBreak: 'break-word'}
};

function caseLabel(item) {
    return item.description?.trim() ? item.description : `Caso ${item.index + 1}`;
}

/**
 * One segment per test: the shape of the whole run at a glance.
 */
export function DlvResultStrip({cases, results}) {
    return (
        <Box
            role="img"
            aria-label={(() => {
                const {passed, failed} = countResults(results);
                return `${passed} test passati, ${failed} falliti su ${cases.length}`;
            })()}
            sx={{display: 'flex', gap: '2px', height: 12}}>
            {cases.map((testCase, index) => {
                const result = results[index];
                const status = !result ? 'non eseguito' : (result.passed ? 'passato' : 'fallito');
                const color = !result ? grey[300] : (result.passed ? green[500] : red[600]);
                return (
                    <Tooltip key={index} title={`${caseLabel({...testCase, index})}: ${status}`} placement="top" arrow>
                        <Box sx={{flex: 1, minWidth: 2, borderRadius: '3px', bgcolor: color}}/>
                    </Tooltip>
                );
            })}
        </Box>
    );
}

/**
 * Headline of a run (counts and progress) with the strip. Also used where a short summary is enough.
 */
export function DlvRunSummary({state}) {
    const {passed, failed} = useMemo(() => countResults(state.results), [state.results]);
    const executed = passed + failed;
    const running = state.status === 'running';

    let headline;
    if (running) {
        headline = `Esecuzione in corso: blocco ${state.chunk.current + 1} di ${state.chunk.count}`;
    } else if (failed > 0) {
        headline = `${failed} ${failed === 1 ? 'test fallito' : 'test falliti'}, ${passed} ${passed === 1 ? 'passato' : 'passati'}`;
    } else {
        headline = `${passed} ${passed === 1 ? 'test passato' : 'test passati'}`;
    }

    return (
        <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" spacing={1}>
                {!running && (failed > 0
                    ? <CancelOutlined sx={{color: red[600]}}/>
                    : <CheckCircle sx={{color: green[600]}}/>)}
                <Typography variant="h3" component="p" aria-live="polite"
                            sx={{color: !running && failed > 0 ? red[700] : 'text.primary'}}>
                    {headline}
                </Typography>
            </Stack>
            <DlvResultStrip cases={state.cases} results={state.results}/>
            {running && <LinearProgress variant="determinate"
                                        value={state.cases.length ? (executed / state.cases.length) * 100 : 0}/>}
        </Stack>
    );
}

function ExpectedAtomChip({check}) {
    return (
        <Chip
            size="small"
            variant="outlined"
            color={check.passed ? 'success' : 'error'}
            icon={check.passed ? <CheckCircleOutline/> : <CancelOutlined/>}
            label={check.expected}
            sx={CODE_CHIP_SX}/>
    );
}

// Everything the program derived for a case, as ASP facts
function AnswerSetDialog({open, onClose, title, atoms}) {
    const {createSnackbar} = useSnackbar();
    const text = useMemo(() => atoms.map(atom => `${atom}.`).join('\n'), [atoms]);

    const handleCopy = async () => {
        const copied = await copyText(text);
        createSnackbar(copied
            ? {message: 'Answer set copiato', type: 'success'}
            : {message: "Non è stato possibile copiare l'answer set", type: 'error'});
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
            <DialogTitle>
                <Stack spacing={0.5}>
                    <span>Answer set</span>
                    <Typography variant="body2" color="text.secondary" component="span">
                        {title}: {atoms.length} {atoms.length === 1 ? 'atomo' : 'atomi'}, in ordine alfabetico
                    </Typography>
                </Stack>
            </DialogTitle>
            <DialogContent dividers sx={{p: 0}}>
                <AspCodeView text={text}/>
            </DialogContent>
            <DialogActions sx={{px: 3, py: 2}}>
                <Button startIcon={<ContentCopyOutlined/>} onClick={handleCopy} sx={{textTransform: 'none'}}>
                    Copia
                </Button>
                <Button variant="contained" onClick={onClose} sx={{textTransform: 'none'}}>Chiudi</Button>
            </DialogActions>
        </Dialog>
    );
}

function CaseRow({item}) {
    const {result} = item;
    const [open, setOpen] = useState(!result.passed);
    const [answerSetOpen, setAnswerSetOpen] = useState(false);
    const bindings = Object.entries(result.bindings ?? {});
    const bodyId = `dlv-case-${item.index}`;

    return (
        <Box>
            <ButtonBase
                onClick={() => setOpen(!open)}
                aria-expanded={open}
                aria-controls={bodyId}
                sx={{width: '100%', justifyContent: 'flex-start', textAlign: 'left', px: 2, py: 1.5, gap: 1.5}}>
                {result.passed
                    ? <CheckCircle sx={{color: green[600]}}/>
                    : <CancelOutlined sx={{color: red[600]}}/>}
                <Box sx={{flex: 1, minWidth: 0}}>
                    <Typography sx={{fontWeight: 600}}>{caseLabel(item)}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {item.description?.trim() ? `Caso ${item.index + 1}, ` : ''}
                        {result.checks.length} {result.checks.length === 1 ? 'atomo atteso' : 'atomi attesi'}
                    </Typography>
                </Box>
                {open ? <ExpandLess color="action"/> : <ExpandMore color="action"/>}
            </ButtonBase>
            <Collapse in={open} timeout="auto" unmountOnExit>
                <Stack id={bodyId} spacing={2} sx={{px: 2, pb: 2, pl: {xs: 2, sm: 6}}}>
                    {result.message &&
                        <Alert severity="error" variant="outlined">{result.message}</Alert>}
                    <Box>
                        <Typography variant="subtitle2" sx={{mb: 1}}>Atomi attesi</Typography>
                        <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
                            {result.checks.map((check, i) => <ExpectedAtomChip key={i} check={check}/>)}
                        </Stack>
                    </Box>
                    {bindings.length > 0 &&
                        <Box>
                            <Typography variant="subtitle2" sx={{mb: 1}}>Valori trovati</Typography>
                            <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
                                {bindings.map(([name, value]) =>
                                    <Chip key={name} size="small" label={`${name} = ${value}`} sx={CODE_CHIP_SX}/>)}
                            </Stack>
                        </Box>}
                    {result.atoms.length > 0 &&
                        <Box>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<ListAltOutlined/>}
                                onClick={() => setAnswerSetOpen(true)}
                                sx={{textTransform: 'none'}}>
                                Mostra answer set ({result.atoms.length})
                            </Button>
                            <AnswerSetDialog
                                open={answerSetOpen}
                                onClose={() => setAnswerSetOpen(false)}
                                title={caseLabel(item)}
                                atoms={result.atoms}/>
                        </Box>}
                </Stack>
            </Collapse>
        </Box>
    );
}

function EmptyState({onImport}) {
    return (
        <Stack alignItems="center" spacing={1.5} sx={{py: 8, px: 3, textAlign: 'center'}}>
            <PlaylistAddCheckOutlined sx={{fontSize: 40, color: 'text.disabled'}}/>
            <Typography variant="h4" component="p">Nessun test eseguito</Typography>
            <Typography color="text.secondary" sx={{maxWidth: 360}}>
                Scrivi i test nell'editor, oppure importa quelli di un altro programma, poi premi Esegui test.
            </Typography>
            <Button variant="outlined" onClick={onImport} sx={{textTransform: 'none'}}>
                Importa test
            </Button>
        </Stack>
    );
}

export function DlvTestResults({state, onResume, onImport}) {
    const [filter, setFilter] = useState('all');

    const items = useMemo(() => {
        const executed = state.cases
            .map((testCase, index) => ({index, description: testCase.description, result: state.results[index]}))
            .filter(item => item.result);
        // Failures first, each group in suite order
        return [...executed.filter(i => !i.result.passed), ...executed.filter(i => i.result.passed)];
    }, [state.cases, state.results]);

    if (state.status === 'idle') {
        return <EmptyState onImport={onImport}/>;
    }

    const {passed, failed} = countResults(state.results);
    const notExecuted = state.cases.length - (passed + failed);
    const visible = filter === 'failed' ? items.filter(i => !i.result.passed) : items;

    return (
        <Stack spacing={2.5} sx={{p: 3}}>
            <DlvRunSummary state={state}/>

            {state.status === 'failed' &&
                <Alert
                    severity="error"
                    action={<Button color="inherit" size="small" onClick={onResume} sx={{textTransform: 'none'}}>
                        Riprova dal blocco {state.error.chunkIndex + 1}
                    </Button>}>
                    {state.error.message}
                </Alert>}
            {state.status === 'stopped' &&
                <Alert severity="info">
                    Esecuzione interrotta: {notExecuted} {notExecuted === 1 ? 'test non eseguito' : 'test non eseguiti'}.
                </Alert>}

            {failed > 0 && passed > 0 &&
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={filter}
                    onChange={(e, value) => value && setFilter(value)}
                    aria-label="Filtra i risultati">
                    <ToggleButton value="all" sx={{textTransform: 'none'}}>Tutti</ToggleButton>
                    <ToggleButton value="failed" sx={{textTransform: 'none'}}>Solo falliti</ToggleButton>
                </ToggleButtonGroup>}

            {visible.length > 0 &&
                <Box sx={{border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden'}}>
                    {visible.map((item, i) =>
                        <React.Fragment key={item.index}>
                            {i > 0 && <Divider/>}
                            <CaseRow item={item}/>
                        </React.Fragment>)}
                </Box>}
        </Stack>
    );
}
